import { useMemo, useState } from "react";
import type { ProjectDetail } from "@/mocks/admin/projects";

type Props = { initial: ProjectDetail["checklist"] };

export default function Checklist({ initial }: Props) {
  const [items, setItems] = useState(initial);

  const grouped = useMemo(() => {
    const map: Record<string, typeof items> = {};
    items.forEach((i) => {
      if (!map[i.category]) map[i.category] = [];
      map[i.category].push(i);
    });
    return map;
  }, [items]);

  const done = items.filter((i) => i.done).length;
  const pct = Math.round((done / items.length) * 100);

  const toggle = (id: string) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, done: !i.done } : i)));
  };

  return (
    <div className="bg-white border border-ink-100 rounded-lg">
      <div className="px-5 pt-5 pb-4 border-b border-ink-100">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display text-base font-bold text-ink-900">Preparation Checklist</h3>
            <p className="text-[11px] text-ink-500 mt-0.5">
              {done} of {items.length} complete
            </p>
          </div>
          <span
            className={`text-xs font-bold px-2.5 py-1 rounded-full ${
              pct === 100
                ? "bg-emerald-100 text-emerald-700"
                : pct >= 70
                  ? "bg-brand-100 text-brand-700"
                  : "bg-amber-100 text-amber-700"
            }`}
          >
            {pct}%
          </span>
        </div>
        <div className="w-full h-2 rounded-full bg-cream-100 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              pct === 100 ? "bg-emerald-500" : "bg-brand-500"
            }`}
            style={{ width: `${pct}%` }}
          ></div>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {Object.entries(grouped).map(([cat, list]) => (
          <div key={cat}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-ink-400 mb-2">
              {cat}
            </div>
            <ul className="space-y-1.5">
              {list.map((c) => (
                <li key={c.id}>
                  <label className="flex items-center gap-3 py-1.5 px-2 -mx-2 rounded-md hover:bg-cream-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={c.done}
                      onChange={() => toggle(c.id)}
                      className="sr-only peer"
                    />
                    <span
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                        c.done
                          ? "bg-brand-500 border-brand-500 text-white"
                          : "border-ink-300 bg-white"
                      }`}
                    >
                      {c.done && <i className="ri-check-line text-xs"></i>}
                    </span>
                    <span
                      className={`flex-1 text-sm ${
                        c.done ? "text-ink-400 line-through" : "text-ink-700"
                      }`}
                    >
                      {c.label}
                    </span>
                    {c.assignee && (
                      <span className="text-[10px] font-semibold text-ink-500 bg-cream-100 px-2 py-0.5 rounded">
                        {c.assignee}
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}